import { Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { useEffect } from 'react';

import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { INTEGRATION_EXCLUDED_ENTITY_TYPES } from '@/modules/assetProviderIntegrations/shared/constants';
import {
  useAssetProviderDisplayName,
  useIsAssetProviderIntegrationEnabled,
} from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { SharedIntegrationEntityTypeahead } from '@/modules/assetProviderIntegrations/shared/SharedIntegrationEntityTypeahead';
import { EntityType } from '@/modules/entities/types/EntityType';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

import { ConnectHouseholdToAssetProviderCard } from '../FullScreenStructuredAssetsModal/ConnectHouseholdToAssetProvider/ConnectHouseholdToAssetProviderCard';
import { useValuationIntegrationDetails } from '../FullScreenStructuredAssetsModal/hooks/useValuationLinkDetails';
import {
  AssetIntegrationFormShape,
  PathsOfAssetIntegrationFormShape,
} from './AssetIntegrationField.types';

export interface AssetIntegrationFieldProps {
  entityType: EntityType;
  householdId: string;
  assetsSum?: Decimal;
  // in scenarios where we don't provide the user the ability to actually add manual assets,
  // we hide the "allow additional manual assets" toggle
  hideManualAssets?: boolean;
  // in scenarios where we're showing this form and there's a near-guarantee of it being an integrated scenario,
  // we want to show the toggles even if the user has not yet connected the entity to an integration
  alwaysShowToggles?: boolean;
}

export function AssetIntegrationField({
  entityType,
  householdId,
  assetsSum,
  hideManualAssets = false,
  alwaysShowToggles = false,
}: AssetIntegrationFieldProps) {
  const { hasConnectedIntegration } = useIsAssetProviderIntegrationEnabled();
  const { isIntegratedScenario } = useValuationIntegrationDetails();
  const { control, setValue } = useFormContext<AssetIntegrationFormShape>();
  const assetProviderDisplayName = useAssetProviderDisplayName({
    isInline: true,
  });
  const isIntegrationSupportedEntityType =
    !INTEGRATION_EXCLUDED_ENTITY_TYPES.includes(entityType);

  const { isHouseholdLinkedToAssetProvider } = useHouseholdDetailsContext();

  const isHouseholdIntegrated = isHouseholdLinkedToAssetProvider;

  const shouldShowIntegrationLink =
    isIntegrationSupportedEntityType && isHouseholdIntegrated;

  const shouldShowConnectHouseholdPrompt =
    isIntegrationSupportedEntityType && // the entity type can be connected to
    !isHouseholdIntegrated && // the household is not already connected to an integration
    hasConnectedIntegration; // the user is logged into an integration

  // when enabling the integration scenario, if there are already structured assets,
  // we want to automatically toggle on the allow additional manual assets switch
  useEffect(() => {
    if (
      isIntegratedScenario &&
      (assetsSum ?? new Decimal(0)).greaterThan(new Decimal(0))
    ) {
      setValue(`allowAdditionalManualAssets`, true);
    }
    // no exhaustive deps because we only want to rerun this if the isIntegratedScenario prop changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntegratedScenario, setValue]);

  if (shouldShowConnectHouseholdPrompt) {
    return <ConnectHouseholdToAssetProviderCard />;
  }

  const showIntegrationToggles = isIntegratedScenario || alwaysShowToggles;
  return (
    <Stack>
      {shouldShowIntegrationLink && (
        <FormLayoutRow>
          <FormLayoutItem>
            <SharedIntegrationEntityTypeahead
              filterToHouseholdId={householdId}
              control={control}
              fieldName={
                `integrationEntityIds` as const satisfies PathsOfAssetIntegrationFormShape
              }
              includeAllEntitiesFieldName={
                `linkToAllAddeparEntities` as const satisfies PathsOfAssetIntegrationFormShape
              }
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {showIntegrationToggles && (
        <>
          {!hideManualAssets && (
            <FormLayoutRow>
              <FormLayoutItem>
                <FormAwareSwitch
                  labelPosition="right"
                  control={control}
                  fieldName={
                    `allowAdditionalManualAssets` as const satisfies PathsOfAssetIntegrationFormShape
                  }
                  label="Manually track additional assets"
                  helpText={`Track assets this entity holds that do not exist in ${assetProviderDisplayName}`}
                />
              </FormLayoutItem>
            </FormLayoutRow>
          )}
          <FormLayoutRow>
            <FormLayoutItem>
              <FormAwareSwitch
                labelPosition="right"
                control={control}
                fieldName={
                  `showAllIntegratedHoldings` as const satisfies PathsOfAssetIntegrationFormShape
                }
                label="List each individual holding"
                helpText="Show all holdings instead of grouping by account"
              />
            </FormLayoutItem>
          </FormLayoutRow>
        </>
      )}
    </Stack>
  );
}
